// =============================================================================
// LIGA AJUPAM — Qué ve un participante que todavía no está "jugando" (v6.11.48)
// =============================================================================
// Antes había dos gates sueltos y desalineados:
//
//   - `!is_season_active` → Inicio mostraba PreSeasonDashboard y el Layout
//     candadeaba Tabla y Partidos.
//   - `blockState.blocked` → Dashboard devolvía una pantalla de bloqueo que
//     tapaba TODO el Inicio, incluida la forma de destrabarse.
//
// Y un tercer caso que no estaba contemplado: el equipo en LISTA_ESPERA veía la
// app completa —tabla, partidos, stats en cero— como si estuviera compitiendo,
// cuando en realidad se anotó en una categoría llena y todavía no entró.
//
// Este módulo unifica los tres en una sola decisión:
//
//   inscriptionHome        El Inicio muestra la vista de inscripción (completar
//                          pareja, pagar, subir comprobante) en vez del Dashboard
//                          de temporada. Es DONDE se regulariza.
//   seasonSectionsLocked   Tabla y Partidos quedan detrás de un velo con el
//                          motivo. No tienen nada que mostrarle a quien no está
//                          compitiendo, y bloquear el Inicio en cambio le sacaba
//                          justo la pantalla que necesitaba.
//
// La prioridad del motivo importa para el copy: primero lo que el jugador puede
// resolver por su cuenta (deuda, pareja), después lo que depende de terceros
// (bloqueo manual, lista de espera, arranque de temporada).

import type { BlockState, BlockReason } from './blockState';

export type SectionLockReason = BlockReason | 'WAITLIST' | 'PRE_SEASON';

export interface SectionAccessInput {
  isSeasonActive?: boolean | null;
  isBracketPublished?: boolean | null;
  /** Vista de solo-lectura de una edición terminada: se ve todo. */
  readOnly?: boolean | null;
  teamStatus?: string | null;
  block: BlockState;
}

export interface SectionAccess {
  inscriptionHome: boolean;
  seasonSectionsLocked: boolean;
  reason: SectionLockReason | null;
}

const OPEN: SectionAccess = { inscriptionHome: false, seasonSectionsLocked: false, reason: null };

export function resolveSectionAccess(input: SectionAccessInput): SectionAccess {
  const { isSeasonActive, isBracketPublished, readOnly, teamStatus, block } = input;

  // Solo-lectura de una edición archivada: es un archivo histórico, se ve entero.
  if (readOnly) return OPEN;

  // El bloqueo manda sobre todo lo demás, pero YA NO tapa el Inicio: ahí es
  // justamente donde el equipo paga o invita al compañero.
  if (block.blocked && block.reason) {
    return { inscriptionHome: true, seasonSectionsLocked: true, reason: block.reason };
  }

  // Inscripto en una categoría que estaba llena: no tiene zona ni partidos.
  if (teamStatus === 'LISTA_ESPERA') {
    return { inscriptionHome: true, seasonSectionsLocked: true, reason: 'WAITLIST' };
  }

  // Antes de que el admin abra la fase regular (mismo gate que ya traía la app).
  if (!isSeasonActive && !isBracketPublished) {
    return { inscriptionHome: true, seasonSectionsLocked: true, reason: 'PRE_SEASON' };
  }

  return OPEN;
}
